import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Page from '../../../components/Layout/Page';
import useGoogleMapsLoader from '../../../hooks/useGoogleMapsLoader';
import useStyles from './styles';
import CustomHeader from '../../../components/General/CustomHeader';
import CustomButton from '../../../components/General/CustomButton';
import SectorsList from '../Layout/component/Sector/SectorsList';
import LayoutMap from '../Layout/component/LayoutMap';
import WeatherTabs from '../AccountHome/components/WeatherTabs';
import { ReduxState } from '../../../types';
import IrrigationProgram from './irrigationProgram';
import getDaysForTwoWeeks from './utils';
import AlignedWeatherForecast from './components/AlignedWeatherForecast';
import RiegoDashboard from './RiegoDashboard';
import RiegoAnalysis from './RiegoAnalysis';
import useEquipoGrouping from './useEquipoGrouping';
import type { RiegoTab } from './riego.types';

const tabPanelSx = {
  pt: 3, flex: 1, minHeight: 0, overflowY: 'auto',
} as const;

const RiegoListView = () => {
  const classes = useStyles();
  const history = useHistory();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const [tab, setTab] = useState<RiegoTab>('vista');

  const days = useMemo(
    () => getDaysForTwoWeeks(dayjs().startOf('week').add(1, 'day')),
    [],
  );

  // Agrupamiento equipo↔sector + sectores regando ahora (Vista General).
  const { grouping, irrigatingSectorIds, loading: groupingLoading } = useEquipoGrouping(
    selectedFarm?.id,
    selectedFarm?.sectors,
  );

  const { isLoaded } = useGoogleMapsLoader();

  const handleIrrigationSeeMore = () => {
    if (selectedSector?.id) {
      localStorage.setItem('sector-tab', '8');
      history.push(`/sectores/${selectedSector.id}?source=riego`
        + `&farmId=${selectedFarm?.id}`);
    }
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <Page
      className={classes.root}
      title={`Riego / ${selectedFarm?.name || 'Campo'}`}
    >
      <Container maxWidth={false} className={classes.containerContainer}>
        <CustomHeader
          title={`Riego / ${selectedFarm?.name || 'Campo'}`}
          showBackButton={false}
          TabsComponent={(
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab value='vista' label='Vista General' />
              <Tab value='dashboard' label='Dashboard Operativo' />
              <Tab value='analisis' label='Análisis por Riegos' />
            </Tabs>
          )}
        />

        {tab === 'dashboard' && (
          <Box sx={tabPanelSx}>
            <RiegoDashboard farmId={selectedFarm?.id} />
          </Box>
        )}

        {tab === 'analisis' && (
          <Box sx={tabPanelSx}>
            <RiegoAnalysis />
          </Box>
        )}

        {tab === 'vista' && (
          <Box display='flex' pt={3} gap={2} flex={1} minHeight={0}>
            <Box className={classes.lateralListsContainer} sx={{ height: '100%' }}>
              <SectorsList
                isLayout={false}
                isIrrigationView={true}
                equipos={grouping?.equipos}
                equipoBySectorId={grouping?.equipoBySectorId}
              />
            </Box>

            <Box flex={1} display='flex' flexDirection='column' gap={3} minWidth={0} sx={{ height: '100%', overflowY: 'auto' }}>
              <Box display='flex' gap={3} height='70vh' minHeight='480px'>
                <Box flex={1} height='100%' display='flex'>
                  {groupingLoading ? (
                    <Skeleton
                      variant='rectangular'
                      sx={{ flex: 1, height: '100%', borderRadius: 1 }}
                    />
                  ) : (
                    <LayoutMap
                      setOpenUploadKMZModal={() => {}}
                      setKMZSector={() => {}}
                      hideDeviceFilters={true}
                      isLayout={false}
                      equipos={grouping?.equipos}
                      equipoBySectorId={grouping?.equipoBySectorId}
                      irrigatingSectorIds={irrigatingSectorIds}
                    />
                  )}
                </Box>

                <Box sx={{ width: '280px', height: '100%' }}>
                  <WeatherTabs
                    showForecastTab={false}
                    showIrrigationDataTab={true}
                    irrigationDays={days}
                    vertical={true}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 'none' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" gutterBottom>
                    Programa de riego
                  </Typography>
                  {selectedSector?.id && (
                    <Box width="120px">
                      <CustomButton
                        buttonText="Ver más"
                        color="blue"
                        onClick={handleIrrigationSeeMore}
                      />
                    </Box>
                  )}
                </Box>
                <IrrigationProgram days={days} readOnly={true} />
              </Box>

              <Box sx={{ flex: 'none', marginBottom: 3 }}>
                <Typography variant="h4" gutterBottom>
                  Clima
                </Typography>
                <AlignedWeatherForecast days={days} />
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Page>
  );
};

export default RiegoListView;
